import React, { useState, useEffect, useCallback } from 'react'
import {
  Box, Tabs, Tab, Alert,
} from '@mui/material'
import { apiService } from '../../../services/api'
import OrangeOpaskaKPIs from './OrangeOpaskaKPIs'
import OrangeOpaskaDailyChart from './OrangeOpaskaDailyChart'
import OrangeOpaskaRegionDistrict from './OrangeOpaskaRegionDistrict'
import OrangeOpaskaSalonTable from './OrangeOpaskaSalonTable'
import OrangeOpaskaLastDaySalon from './OrangeOpaskaLastDaySalon'
import OrangeOpaskaKAMHistory from './OrangeOpaskaKAMHistory'
import OrangeOpaskaTrend from './OrangeOpaskaTrend'
import OrangeOpaskaRawData from './OrangeOpaskaRawData'
import {
  ReportShell, HeaderButton, FilterBar, Icon,
} from '../../report-shell'

const monthNames = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
]

const OrangeOpaskaSales = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [activeTab, setActiveTab] = useState(0)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState(null)

  // Data cache per endpoint
  const [kpisData, setKpisData] = useState(null)
  const [kpisLoading, setKpisLoading] = useState(false)
  const [kpisError, setKpisError] = useState(null)

  const [breakdownData, setBreakdownData] = useState(null)
  const [breakdownLoading, setBreakdownLoading] = useState(false)
  const [breakdownError, setBreakdownError] = useState(null)

  const [kamHistoryData, setKamHistoryData] = useState(null)
  const [kamHistoryLoading, setKamHistoryLoading] = useState(false)
  const [kamHistoryError, setKamHistoryError] = useState(null)

  const fetchKpis = useCallback(async () => {
    try { setKpisLoading(true); setKpisError(null)
      const response = await apiService.getOrangeOpaskaKpis()
      setKpisData(response.data)
    } catch (err) { setKpisError(`Błąd pobierania KPI: ${err.message}`) }
    finally { setKpisLoading(false) }
  }, [])

  const fetchBreakdown = useCallback(async () => {
    try { setBreakdownLoading(true); setBreakdownError(null)
      const response = await apiService.getOrangeOpaskaSalesBreakdown(selectedMonth, selectedYear)
      setBreakdownData(response.data)
    } catch (err) { setBreakdownError(`Błąd pobierania danych: ${err.message}`) }
    finally { setBreakdownLoading(false) }
  }, [selectedMonth, selectedYear])

  const fetchKamHistory = useCallback(async () => {
    try { setKamHistoryLoading(true); setKamHistoryError(null)
      const response = await apiService.getOrangeOpaskaKamHistory(selectedMonth, selectedYear)
      setKamHistoryData(response.data)
    } catch (err) { setKamHistoryError(`Błąd pobierania historii: ${err.message}`) }
    finally { setKamHistoryLoading(false) }
  }, [selectedMonth, selectedYear])

  useEffect(() => {
    fetchKpis()
    setBreakdownData(null)
    setKamHistoryData(null)
  }, [fetchKpis])

  useEffect(() => {
    if (activeTab >= 1 && activeTab <= 4 && !breakdownData && !breakdownLoading) fetchBreakdown()
    if (activeTab === 5 && !kamHistoryData && !kamHistoryLoading) fetchKamHistory()
    if (activeTab === 6 && !breakdownData && !breakdownLoading) fetchBreakdown()
  }, [activeTab, breakdownData, breakdownLoading, kamHistoryData, kamHistoryLoading, fetchBreakdown, fetchKamHistory])

  const exportExcel = async () => {
    try { setExporting(true); setExportError(null)
      const response = await apiService.getOrangeOpaskaExport(selectedMonth, selectedYear)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `Opaska_SOS_${monthNames[selectedMonth - 1]}_${selectedYear}.xlsx`)
      document.body.appendChild(link); link.click(); link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) { setExportError(`Błąd eksportu: ${err.message}`) }
    finally { setExporting(false) }
  }

  const years = []
  const currentYear = new Date().getFullYear()
  for (let y = currentYear; y >= currentYear - 2; y--) years.push(y)

  const periods = [
    {
      type: 'select', key: 'month', label: 'Miesiąc',
      value: selectedMonth, display: monthNames[selectedMonth - 1],
      options: monthNames.map((n, i) => ({ value: i + 1, label: n })),
      onChange: (v) => setSelectedMonth(parseInt(v)),
      allowClear: false,
    },
    {
      type: 'select', key: 'year', label: 'Rok',
      value: selectedYear, display: String(selectedYear),
      options: years.map(y => ({ value: y, label: String(y) })),
      onChange: (v) => setSelectedYear(parseInt(v)),
      allowClear: false,
    },
  ]

  return (
    <ReportShell
      title="Orange — Opaska SOS · Sprzedaż"
      subtitle="Sprzedaż produktu Opaska SOS — agregaty i drill-down per KAM, salon, agent."
      breadcrumb={['KAM-SFA', 'Orange', 'Opaska SOS', 'Sprzedaż']}
      accentColor="#ff7900"
      actions={
        <HeaderButton primary onClick={exportExcel} disabled={exporting}>
          <Icon.download /> {exporting ? 'Eksport…' : 'Eksport Excel'}
        </HeaderButton>
      }
      filterBar={<FilterBar periods={periods} />}
    >
      {exportError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>{exportError}</Alert>}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newVal) => setActiveTab(newVal)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Podsumowanie" />
          <Tab label="Sprzedaż dzienna" />
          <Tab label="Regiony i dystrykty" />
          <Tab label="Salony" />
          <Tab label="Ost. dzień per salon" />
          <Tab label="Per KAM" />
          <Tab label="Trend" />
          <Tab label="Dane surowe" />
        </Tabs>
      </Box>

      {activeTab === 0 && <OrangeOpaskaKPIs data={kpisData} loading={kpisLoading} error={kpisError} />}
      {activeTab === 1 && <OrangeOpaskaDailyChart data={breakdownData} loading={breakdownLoading} error={breakdownError} />}
      {activeTab === 2 && <OrangeOpaskaRegionDistrict data={breakdownData} loading={breakdownLoading} error={breakdownError} />}
      {activeTab === 3 && <OrangeOpaskaSalonTable data={breakdownData} loading={breakdownLoading} error={breakdownError} />}
      {activeTab === 4 && <OrangeOpaskaLastDaySalon data={breakdownData} loading={breakdownLoading} error={breakdownError} />}
      {activeTab === 5 && <OrangeOpaskaKAMHistory data={kamHistoryData} loading={kamHistoryLoading} error={kamHistoryError} />}
      {activeTab === 6 && <OrangeOpaskaTrend data={breakdownData} loading={breakdownLoading} error={breakdownError} />}
      {activeTab === 7 && <OrangeOpaskaRawData />}
    </ReportShell>
  )
}

export default OrangeOpaskaSales
